import {
	useState,
	useEffect,
	useCallback,
	useRef,
	useLayoutEffect,
} from 'react';
import {
	DownOutlined,
	CopyOutlined,
	CloseOutlined,
	UserOutlined,
	ProfileOutlined,
	ThunderboltOutlined,
	SwapOutlined,
} from '@ant-design/icons';
import { Select, message, Popover, Input } from 'antd';
import { USER_ROLES } from 'src/_shared/constants';
import { COLORS } from 'src/_shared/styles/colors';
import { ListCompanies } from 'src/_shared/navigation/aside/listCompaniesGraphQL';
import gql from 'graphql-tag';
import {
	UpdateUserAside,
	GetUser,
	GetFeatureFlagsByUser,
} from 'src/_shared/api/graphql/custom/users';
import ElevatedPermissionsToggle from './ElevatedPermissionsToggle.jsx';
import { useSelector, useDispatch } from 'react-redux';
import {
	createSetCurrentUserAction,
	updateDisplayAs,
	updateFeatureFlags as updateFeatureFlagsAction,
} from 'src/_shared/user/userActions.js';
import { resetReferral as resetReferralAction } from 'src/_shared/referral/referralActions.js';
import { useApolloClient } from '@apollo/client';
import { persistor } from 'src/store';
import { TAB_ID } from 'src/_shared/broadcast-tab-id.js';
import { isPhone, onPhoneChange } from 'src/_shared/phone.js';

const QueryCompanyEntities = gql`
	query QueryCompanyEntities($companyId: ID!) {
		queryCompanyEntities(companyId: $companyId) {
			companyId
			departments {
				id
				name
			}
			subCompanies {
				id
				name
			}
			userGroups {
				id
				name
			}
			metadata
		}
	}
`;

const parse = (input) => {
	try {
		if (typeof input === 'string') {
			return JSON.parse(input);
		} else {
			return input;
		}
	} catch {
		return input;
	}
};

// Below the app's phone breakpoint the panel docks under the header's toggle
// button instead of floating: a saved desktop position lands off screen on a
// phone and the mouse-only drag can't bring it back. Larger screens keep the
// draggable panel.
const DOCK_GAP = 8;

const useDocked = () => {
	const [docked, setDocked] = useState(isPhone);
	useEffect(() => onPhoneChange(setDocked), []);
	return docked;
};

// Top edge for the docked panel: just under the header's toggle button, or
// just under the top edge once the header has scrolled away.
const dockedTop = () => {
	const rect = document
		.querySelector('.header-right .elevated-permissions-toggle')
		?.getBoundingClientRect();
	return Math.max(DOCK_GAP, Math.round((rect?.bottom ?? 0) + DOCK_GAP));
};

const ElevatedPermissionsControls = ({
	history,
	showAdminControls,
	toggleAdminControls,
}) => {
	const docked = useDocked();
	const [dockTop, setDockTop] = useState(dockedTop);
	const currentUser = useSelector((state) => state?.user?.currentUser);
	const broadcastChannel = useRef(null);

	useEffect(() => {
		broadcastChannel.current = new BroadcastChannel('erin-elevated-sync');
		broadcastChannel.current.onmessage = (event) => {
			if (event.data?.type === 'USER_CHANGED' && event.data?.tabId !== TAB_ID) {
				window.location.reload();
			}
		};

		return () => {
			broadcastChannel.current?.close();
		};
	}, []);

	const client = useApolloClient();
	const dispatch = useDispatch();
	const setCurrentUser = (user) => {
		dispatch(createSetCurrentUserAction(user));
	};
	const updateFeatureFlags = (featureFlags) => {
		dispatch(updateFeatureFlagsAction(featureFlags));
	};
	const resetReferral = () => {
		dispatch(resetReferralAction());
	};
	const updateDisplay = (role) => {
		dispatch(updateDisplayAs(role));
	};

	const [companies, setCompanies] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [subCompanies, setSubCompanies] = useState([]);
	const [userGroups, setUserGroups] = useState([]);
	const elevatedPermissions =
		currentUser?.admin && parse(currentUser.admin)?.permissions === '*';
	const [position, setPosition] = useState(() => {
		const saved = localStorage.getItem('elevated-permissions-position');
		return saved
			? JSON.parse(saved)
			: { x: window.innerWidth - 290, y: window.innerHeight - 200 };
	});
	const [isDragging, setIsDragging] = useState(false);
	const dragOffset = useRef({ x: 0, y: 0 });
	const isMountedRef = useRef(false);

	const broadcastUserChanged = useCallback(() => {
		broadcastChannel.current?.postMessage({
			type: 'USER_CHANGED',
			tabId: TAB_ID,
		});
	}, []);

	const [autoAssign, setAutoAssign] = useState(() => {
		const saved = localStorage.getItem('elevated-permissions-auto-assign');
		return saved
			? JSON.parse(saved)
			: { department: false, subCompany: false, userGroup: false };
	});

	const toggleAutoAssign = useCallback((field) => {
		setAutoAssign((prev) => {
			const next = { ...prev, [field]: !prev[field] };
			localStorage.setItem(
				'elevated-permissions-auto-assign',
				JSON.stringify(next)
			);
			return next;
		});
	}, []);

	const [switchUserOpen, setSwitchUserOpen] = useState(false);
	const [switchUserLoading, setSwitchUserLoading] = useState(false);
	const switchUserInputRef = useRef(null);

	const handleSwitchUser = useCallback(
		async (userId) => {
			const trimmed = userId.trim();
			if (!trimmed) return;
			setSwitchUserLoading(true);
			const originalUserId = currentUser.id;
			try {
				const { data } = await client.query({
					query: GetUser,
					variables: { id: trimmed },
					fetchPolicy: 'network-only',
				});

				if (data?.getUser) {
					handleClearCache(resetReferral);
					setCurrentUser({ ...data.getUser });
					await persistor.flush();
					localStorage.setItem(
						'support-login-original-user-id',
						originalUserId
					);
					setSwitchUserOpen(false);
					broadcastUserChanged();
					message.success(
						`Switched to ${data.getUser.firstName} ${data.getUser.lastName}`
					);
					history.push('/');
				} else {
					message.error('User not found');
				}
			} catch (error) {
				message.error('Failed to switch user');
				console.error('Switch user error:', error);
			} finally {
				setSwitchUserLoading(false);
			}
		},
		// optional-chained: deps evaluate during render, which can happen while the jwt cookie says
		// "authenticated" but redux currentUser is still null (fresh origin, pre-rehydration) — the
		// null-user early return below runs too late to protect these.
		[client, currentUser?.id, setCurrentUser]
	);

	const listCompanies = useCallback(
		async (nextToken = null, companiesList = []) => {
			try {
				const { data } = await client.query({
					query: ListCompanies,
					variables: {
						limit: 1000,
						nextToken,
					},
					fetchPolicy: 'network-only',
				});
				const fetched = data?.listCompanies?.items || [];
				const token = data?.listCompanies?.nextToken;
				let newCompaniesList = [...companiesList, ...fetched];

				if (token) {
					return await listCompanies(token, newCompaniesList);
				}

				newCompaniesList = newCompaniesList.sort((a, b) => {
					if (!a.disableSite !== !b.disableSite) {
						return a.disableSite ? 1 : -1;
					}
					const order = ['pro', 'partner', null, 'free'];
					const accountComparison =
						order.indexOf(a.accountType) - order.indexOf(b.accountType);
					if (accountComparison !== 0) {
						return accountComparison;
					}
					return a.name.localeCompare(b.name);
				});
				return newCompaniesList;
			} catch (error) {
				console.log('listCompanies Error:', error);
				return companiesList;
			}
		},
		[client]
	);

	const refetchFeatureFlags = useCallback(
		async (userId) => {
			const { data } = await client.query({
				query: GetFeatureFlagsByUser,
				variables: { userId: userId },
			});
			return data?.getFeatureFlagsByUser || [];
		},
		[client]
	);

	const fetchCompanyEntities = useCallback(
		async (companyId) => {
			const { data } = await client.query({
				query: QueryCompanyEntities,
				variables: { companyId: companyId },
			});
			return data?.queryCompanyEntities || {};
		},
		[client]
	);

	const handleClearCache = useCallback(
		(resetReferralFn) => {
			client.clearStore();
			if (resetReferralFn) {
				resetReferralFn();
			}
		},
		[client]
	);

	const onChangeAttribute = useCallback(
		async ({
			companyId,
			departmentId,
			subCompanyId,
			userGroupId,
			clearSubCompany = false,
		}) => {
			handleClearCache(resetReferral);

			[
				'whiteLabel',
				'loaderURL',
				'supportsSVG',
				'errorImageURL',
				'badgeList-Anniversary',
				'badgeList-Nominated',
				'badgeList-On-the-Spot',
				'badgeList-myBadges',
			].forEach((key) => localStorage.removeItem(key));

			const input = { id: currentUser.id };
			if (companyId) input.companyId = companyId;
			if (departmentId) input.departmentId = departmentId;
			if (subCompanyId) input.subCompanyId = subCompanyId;
			if (userGroupId) input.userGroupId = userGroupId;
			if (clearSubCompany) input.subCompanyId = null;

			try {
				const { data } = await client.mutate({
					mutation: UpdateUserAside,
					variables: {
						input: input,
					},
				});
				const userUpdatedCompany = {
					...currentUser,
					companyId: data.updateUser.companyId,
					departmentId: data.updateUser.departmentId,
					department: data.updateUser.department,
					subCompanyId: data.updateUser.subCompanyId,
					subCompany: data.updateUser.subCompany,
					userGroupId: data.updateUser.userGroupId,
					userGroup: data.updateUser.userGroup,
					company: data.updateUser.company,
				};
				setCurrentUser(userUpdatedCompany);
				if (companyId) {
					const companyEntities = await fetchCompanyEntities(companyId);
					const sortedDepts = [...(companyEntities.departments || [])].sort(
						(a, b) => a.name.localeCompare(b.name)
					);
					const sortedSubCos = [...(companyEntities.subCompanies || [])].sort(
						(a, b) => a.name.localeCompare(b.name)
					);
					const sortedUGs = [...(companyEntities.userGroups || [])].sort(
						(a, b) => a.name.localeCompare(b.name)
					);
					setDepartments(sortedDepts);
					setSubCompanies(sortedSubCos);
					setUserGroups(sortedUGs);

					// Auto-assign entities if enabled
					const autoInput = { id: currentUser.id };
					let hasAutoUpdates = false;

					if (autoAssign.department && sortedDepts.length > 0) {
						const match =
							sortedDepts.find((d) => d.name.toLowerCase() === 'other') ||
							sortedDepts[0];
						autoInput.departmentId = match.id;
						hasAutoUpdates = true;
					}

					if (autoAssign.subCompany) {
						if (sortedSubCos.length > 0) {
							autoInput.subCompanyId = sortedSubCos[0].id;
						} else {
							autoInput.subCompanyId = null;
						}
						hasAutoUpdates = true;
					}

					if (autoAssign.userGroup && sortedUGs.length > 0) {
						const match =
							sortedUGs.find((ug) => ug.name.toLowerCase() === 'default') ||
							sortedUGs[0];
						autoInput.userGroupId = match.id;
						hasAutoUpdates = true;
					}

					if (hasAutoUpdates) {
						const { data: autoData } = await client.mutate({
							mutation: UpdateUserAside,
							variables: { input: autoInput },
						});
						setCurrentUser({
							...userUpdatedCompany,
							departmentId: autoData.updateUser.departmentId,
							department: autoData.updateUser.department,
							subCompanyId: autoData.updateUser.subCompanyId,
							subCompany: autoData.updateUser.subCompany,
							userGroupId: autoData.updateUser.userGroupId,
							userGroup: autoData.updateUser.userGroup,
						});
					}
				}
				const featureFlags = await refetchFeatureFlags(currentUser.id);
				updateFeatureFlags(featureFlags);
				broadcastUserChanged();
				// Force navigation to trigger re-render
				setTimeout(() => {
					history.push('/dashboard');
				}, 0);
			} catch (error) {
				console.error('Error changing company:', error);
			}
		},
		[
			currentUser,
			client,
			setCurrentUser,
			updateFeatureFlags,
			resetReferral,
			history,
			handleClearCache,
			refetchFeatureFlags,
			autoAssign,
		]
	);

	const onChangeRole = useCallback(
		async (role) => {
			handleClearCache(resetReferral);

			try {
				await client.mutate({
					mutation: UpdateUserAside,
					variables: {
						input: {
							id: currentUser.id,
							role,
						},
					},
				});
				const featureFlags = await refetchFeatureFlags(currentUser.id);
				updateDisplay(role);
				setCurrentUser({
					...currentUser,
					role: role,
					featureFlags: featureFlags,
				});
				broadcastUserChanged();
				// Force navigation to trigger re-render
				setTimeout(() => {
					history.push('/dashboard');
				}, 0);
			} catch (error) {
				console.error('Error changing role:', error);
			}
		},
		[
			currentUser,
			client,
			setCurrentUser,
			updateDisplay,
			resetReferral,
			history,
			handleClearCache,
			refetchFeatureFlags,
		]
	);

	const onToggleScopedUser = useCallback(async () => {
		handleClearCache(resetReferral);
		const newValue = !Boolean(currentUser?.isScopedUser);

		try {
			await client.mutate({
				mutation: UpdateUserAside,
				variables: {
					input: {
						id: currentUser.id,
						isScopedUser: newValue,
					},
				},
			});
			const featureFlags = await refetchFeatureFlags(currentUser.id);
			setCurrentUser({
				...currentUser,
				isScopedUser: newValue,
				featureFlags: featureFlags,
			});
			broadcastUserChanged();
			// Force navigation to trigger re-render
			setTimeout(() => {
				history.push('/dashboard');
			}, 0);
		} catch (error) {
			console.error('Error changing scoped permissions:', error);
		}
	}, [
		currentUser,
		client,
		setCurrentUser,
		resetReferral,
		history,
		handleClearCache,
		refetchFeatureFlags,
	]);

	const handleSelectFocus = useCallback(async () => {
		if (companies.length === 0) {
			const allCompaniesData = await listCompanies();
			if (isMountedRef.current) {
				setCompanies(allCompaniesData);
			}
		}
	}, [companies.length, listCompanies]);

	const handleCopyId = useCallback(
		async (e, idType) => {
			e.stopPropagation();
			try {
				await navigator.clipboard.writeText(currentUser[idType]);
				message.success(
					`${idType === 'id' ? 'userId' : idType} copied to your clipboard`
				);
			} catch (err) {
				console.error(`Failed to copy ${idType}:`, err);
			}
		},
		[
			currentUser?.companyId,
			currentUser?.subCompanyId,
			currentUser?.id,
			currentUser?.departmentId,
			currentUser?.userGroupId,
		]
	);

	const handleMouseDown = useCallback(
		(e) => {
			if (docked || e.button !== 0) return;
			setIsDragging(true);
			dragOffset.current = {
				x: e.clientX - position.x,
				y: e.clientY - position.y,
			};
			e.preventDefault();
		},
		[docked, position]
	);

	useEffect(() => {
		if (!isDragging) return;

		const handleMouseMove = (e) => {
			const newX = e.clientX - dragOffset.current.x;
			const newY = e.clientY - dragOffset.current.y;

			const maxX = window.innerWidth - 250;
			const maxY = window.innerHeight - 100;

			setPosition({
				x: Math.max(0, Math.min(newX, maxX)),
				y: Math.max(0, Math.min(newY, maxY)),
			});
		};

		const handleMouseUp = () => {
			setIsDragging(false);
			localStorage.setItem(
				'elevated-permissions-position',
				JSON.stringify(position)
			);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isDragging, position]);

	useLayoutEffect(() => {
		const handleResize = () => {
			setPosition((prev) => ({
				x: Math.min(prev.x, window.innerWidth - 250),
				y: Math.min(prev.y, window.innerHeight - 100),
			}));
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useLayoutEffect(() => {
		if (!docked || !showAdminControls) return undefined;
		const update = () => setDockTop(dockedTop());
		update();
		// The page scrolls inside .container-fluid, not the window: capture.
		window.addEventListener('resize', update);
		window.addEventListener('scroll', update, { passive: true, capture: true });
		return () => {
			window.removeEventListener('resize', update);
			window.removeEventListener('scroll', update, { capture: true });
		};
	}, [docked, showAdminControls]);

	useEffect(() => {
		isMountedRef.current = true;

		const fetchCompanies = async () => {
			if (currentUser?.admin && parse(currentUser.admin)?.permissions === '*') {
				const allCompaniesData = await listCompanies();
				const companyEntities = await fetchCompanyEntities(
					currentUser.companyId
				);
				if (isMountedRef.current) {
					setCompanies(allCompaniesData);

					let departmentsList = companyEntities.departments || [];
					let subCompaniesList = companyEntities.subCompanies || [];
					let userGroupsList = companyEntities.userGroups || [];

					if (
						currentUser?.department?.companyId &&
						currentUser.department.companyId !== currentUser.companyId &&
						!departmentsList.find((d) => d.id === currentUser.department.id)
					) {
						departmentsList = [
							...departmentsList,
							{
								id: currentUser.department.id,
								name: currentUser.department.name,
							},
						];
					}

					if (
						currentUser?.subCompany?.companyId &&
						currentUser.subCompany.companyId !== currentUser.companyId &&
						!subCompaniesList.find((sc) => sc.id === currentUser.subCompany.id)
					) {
						subCompaniesList = [
							...subCompaniesList,
							{
								id: currentUser.subCompany.id,
								name: currentUser.subCompany.name,
							},
						];
					}

					if (
						currentUser?.userGroup?.companyId &&
						currentUser.userGroup.companyId !== currentUser.companyId &&
						!userGroupsList.find((ug) => ug.id === currentUser.userGroup.id)
					) {
						userGroupsList = [
							...userGroupsList,
							{
								id: currentUser.userGroup.id,
								name: currentUser.userGroup.name,
							},
						];
					}

					setDepartments(
						[...departmentsList].sort((a, b) => a.name.localeCompare(b.name))
					);
					setSubCompanies(
						[...subCompaniesList].sort((a, b) => a.name.localeCompare(b.name))
					);
					setUserGroups(
						[...userGroupsList].sort((a, b) => a.name.localeCompare(b.name))
					);
				}
			}
		};

		fetchCompanies();

		return () => {
			isMountedRef.current = false;
		};
	}, [currentUser, listCompanies, fetchCompanyEntities]);

	if (
		!currentUser?.id ||
		!elevatedPermissions ||
		companies.length === 0 ||
		!showAdminControls
	)
		return null;

	return (
		<div
			id="elevated-permissions-container"
			className={docked ? 'docked' : undefined}
			style={{
				...(docked
					? {
							left: 'auto',
							right: `${DOCK_GAP}px`,
							top: `max(${dockTop}px, calc(env(safe-area-inset-top, 0px) + ${DOCK_GAP}px))`,
							width: `min(320px, calc(100vw - ${DOCK_GAP * 2}px))`,
							// Clear the bottom edge, and the mobile shell's tab bar.
							maxHeight: `calc(100vh - ${dockTop + DOCK_GAP}px - var(--tab-bar-height, 0px) - var(--safe-bottom, 0px))`,
							overflowY: 'auto',
						}
					: { left: `${position.x}px`, top: `${position.y}px` }),
				boxShadow: isDragging
					? '0 8px 24px rgba(0,0,0,0.25)'
					: '0 4px 12px rgba(0,0,0,0.15)',
				transition: isDragging ? 'none' : 'box-shadow 0.2s ease',
			}}
		>
			<div
				className="elevated-permissions-title-bar"
				onMouseDown={handleMouseDown}
				style={{
					cursor: docked ? 'default' : isDragging ? 'grabbing' : 'grab',
				}}
			>
				<div className="elevated-permissions-title">
					<ElevatedPermissionsToggle
						toggleAdminControls={toggleAdminControls}
						showAdminControls={showAdminControls}
						currentUser={currentUser}
						style={{ margin: 0 }}
					/>
					<span>Super Admin Controls</span>
				</div>
				<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
					<Popover
						open={switchUserOpen}
						onOpenChange={(open) => {
							setSwitchUserOpen(open);
							if (open) {
								setTimeout(() => switchUserInputRef.current?.focus(), 0);
							}
						}}
						trigger="click"
						getPopupContainer={() =>
							document.getElementById('elevated-permissions-container')
						}
						content={
							<div onMouseDown={(e) => e.stopPropagation()}>
								<Input
									ref={switchUserInputRef}
									placeholder="Paste User ID and press Enter"
									size="small"
									style={{ width: 300 }}
									disabled={switchUserLoading}
									onPressEnter={(e) => handleSwitchUser(e.target.value)}
								/>
							</div>
						}
					>
						<SwapOutlined
							className="elevated-permissions-user-icon"
							title="Support Login as User by ID"
							onMouseEnter={(e) => (e.currentTarget.style.color = '#1e9e28e6')}
							onMouseLeave={(e) => (e.currentTarget.style.color = '#05031be6')}
						/>
					</Popover>
					<UserOutlined
						className="elevated-permissions-user-icon"
						onClick={(e) => handleCopyId(e, 'id')}
						title={`Copy User ID: ${currentUser?.id}`}
						onMouseEnter={(e) => (e.currentTarget.style.color = '#1e9e28e6')}
						onMouseLeave={(e) => (e.currentTarget.style.color = '#05031be6')}
					/>
				</div>
			</div>
			<div>
				<div className="elevated-permissions-label-container">
					<span className="elevated-permissions-label-name">Company</span>
					<CopyOutlined
						onClick={(e) => handleCopyId(e, 'companyId')}
						className="elevated-permissions-label-copy"
						title={`Copy Company ID: ${currentUser.companyId} (Cred: Sam Smith)`}
						onMouseEnter={(e) => (e.currentTarget.style.color = '#1e9e28e6')}
						onMouseLeave={(e) => (e.currentTarget.style.color = '#05031be6')}
					/>
				</div>
				<Select
					showSearch
					style={{ width: '100%' }}
					placeholder="Select Company"
					optionFilterProp="children"
					value={currentUser.company.id}
					suffixIcon={<DownOutlined />}
					getPopupContainer={() =>
						document.getElementById('elevated-permissions-container')
					}
					dropdownStyle={{ zIndex: 10001 }}
					onChange={(companyId) => onChangeAttribute({ companyId: companyId })}
					onFocus={handleSelectFocus}
				>
					{companies.map((company) => {
						const color = COLORS.epicPurple;
						let accountType = company.accountType
							? `(${company.accountType})`
							: '';
						if (company?.disableSite) accountType = `${accountType} (disabled)`;
						return (
							<Select.Option
								key={company.id}
								value={company.id}
								label={company.name}
								style={{ color }}
							>
								{company.name}
								<strong>{`  ${accountType}`}</strong>
							</Select.Option>
						);
					})}
				</Select>
			</div>
			<div>
				<div className="elevated-permissions-label-container">
					<span className="elevated-permissions-label-name">Role</span>
					{currentUser?.scopedPolicy ? (
						<ProfileOutlined
							className="elevated-permissions-label-copy"
							style={{
								color: currentUser?.isScopedUser ? '#6cb761' : '#05031be6',
							}}
							onClick={onToggleScopedUser}
							title={`Toggle Scoped Permissions - Currently: ${Boolean(currentUser?.isScopedUser)}`}
							onMouseEnter={(e) => (e.currentTarget.style.color = '#1e9e28e6')}
							onMouseLeave={(e) =>
								(e.currentTarget.style.color = currentUser?.isScopedUser
									? '#6cb761'
									: '#05031be6')
							}
						/>
					) : null}
				</div>
				<Select
					style={{ width: '100%' }}
					placeholder="Select Role"
					optionFilterProp="children"
					value={currentUser?.role}
					suffixIcon={<DownOutlined />}
					getPopupContainer={() =>
						document.getElementById('elevated-permissions-container')
					}
					dropdownStyle={{ zIndex: 10001 }}
					onChange={onChangeRole}
				>
					<Select.Option
						key={USER_ROLES.EXTENDED_USER}
						value={USER_ROLES.EXTENDED_USER}
					>
						Extended User
					</Select.Option>
					<Select.Option key={USER_ROLES.EMPLOYEE} value={USER_ROLES.EMPLOYEE}>
						Employee
					</Select.Option>
					<Select.Option key={USER_ROLES.MANAGER} value={USER_ROLES.MANAGER}>
						Manager
					</Select.Option>
					<Select.Option key={USER_ROLES.ADMIN} value={USER_ROLES.ADMIN}>
						Admin
					</Select.Option>
					<Select.Option
						key={USER_ROLES.SUPER_ADMIN}
						value={USER_ROLES.SUPER_ADMIN}
					>
						Super Admin
					</Select.Option>
				</Select>
			</div>
			<div>
				<div className="elevated-permissions-label-container">
					<span className="elevated-permissions-label-name">Department</span>
					<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
						<ThunderboltOutlined
							className="elevated-permissions-label-copy"
							onClick={() => toggleAutoAssign('department')}
							style={{
								color: autoAssign.department ? '#6cb761' : '#05031be6',
							}}
							title={`Auto-assign Department (looks for "Other") - Currently: ${autoAssign.department ? 'ON' : 'OFF'}`}
							onMouseEnter={(e) => (e.currentTarget.style.color = '#1e9e28e6')}
							onMouseLeave={(e) =>
								(e.currentTarget.style.color = autoAssign.department
									? '#6cb761'
									: '#05031be6')
							}
						/>
						<CopyOutlined
							className="elevated-permissions-label-copy"
							onClick={(e) => handleCopyId(e, 'departmentId')}
							title={`Copy Department ID: ${currentUser?.departmentId}`}
							onMouseEnter={(e) => (e.currentTarget.style.color = '#1e9e28e6')}
							onMouseLeave={(e) => (e.currentTarget.style.color = '#05031be6')}
						/>
					</div>
				</div>
				<Select
					showSearch
					style={{
						width: '100%',
						...(currentUser?.department?.companyId &&
						currentUser?.department?.companyId !== currentUser.companyId
							? { color: 'red' }
							: {}),
					}}
					placeholder="Select Department"
					optionFilterProp="children"
					value={currentUser.departmentId}
					suffixIcon={<DownOutlined />}
					getPopupContainer={() =>
						document.getElementById('elevated-permissions-container')
					}
					dropdownStyle={{ zIndex: 10001 }}
					onChange={(departmentId) =>
						onChangeAttribute({ departmentId: departmentId })
					}
				>
					{departments.map((department) => {
						const isMismatched =
							currentUser?.department?.id === department.id &&
							currentUser?.department?.companyId !== currentUser.companyId;
						return (
							<Select.Option
								key={department.id}
								value={department.id}
								label={department.name}
								style={isMismatched ? { color: 'red' } : {}}
							>
								{department.name}
							</Select.Option>
						);
					})}
				</Select>
			</div>
			<div>
				<div className="elevated-permissions-label-container">
					<div
						style={{ display: 'flex', alignItems: 'center', marginBottom: '0' }}
					>
						<span className="elevated-permissions-label-name">Sub Company</span>
						{currentUser.subCompanyId && (
							<CloseOutlined
								onClick={() => {
									onChangeAttribute({ clearSubCompany: true });
								}}
								className="elevated-permissions-remove-icon"
								onMouseEnter={(e) =>
									(e.currentTarget.style.background = 'oklch(83% 0.120 21.700)')
								}
								onMouseLeave={(e) =>
									(e.currentTarget.style.background = 'oklch(64% 0.260 28.300)')
								}
								title="Remove Sub Company"
							/>
						)}
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
						<ThunderboltOutlined
							className="elevated-permissions-label-copy"
							onClick={() => toggleAutoAssign('subCompany')}
							style={{
								color: autoAssign.subCompany ? '#6cb761' : '#05031be6',
							}}
							title={`Auto-assign Sub Company (first available, or clear) - Currently: ${autoAssign.subCompany ? 'ON' : 'OFF'}`}
							onMouseEnter={(e) => (e.currentTarget.style.color = '#1e9e28e6')}
							onMouseLeave={(e) =>
								(e.currentTarget.style.color = autoAssign.subCompany
									? '#6cb761'
									: '#05031be6')
							}
						/>
						<CopyOutlined
							className="elevated-permissions-label-copy"
							onClick={(e) => {
								if (currentUser.subCompanyId) {
									handleCopyId(e, 'subCompanyId');
								}
							}}
							style={{
								color: currentUser.subCompanyId ? '#05031be6' : '#05031b55',
								cursor: currentUser.subCompanyId ? 'copy' : 'not-allowed',
							}}
							title={
								currentUser.subCompanyId
									? `Copy Sub Company ID: ${currentUser?.subCompanyId}`
									: 'No Sub Company ID to Copy'
							}
							onMouseEnter={(e) => {
								if (currentUser.subCompanyId) {
									e.currentTarget.style.color = '#1e9e28e6';
								}
							}}
							onMouseLeave={(e) => {
								if (currentUser.subCompanyId) {
									e.currentTarget.style.color = '#05031be6';
								}
							}}
						/>
					</div>
				</div>
				<Select
					showSearch
					style={{
						width: '100%',
						...(currentUser?.subCompany?.companyId &&
						currentUser?.subCompany?.companyId !== currentUser.companyId
							? { color: 'red' }
							: {}),
					}}
					placeholder="Select Sub Company"
					optionFilterProp="children"
					value={currentUser.subCompanyId}
					suffixIcon={<DownOutlined />}
					getPopupContainer={() =>
						document.getElementById('elevated-permissions-container')
					}
					dropdownStyle={{ zIndex: 10001 }}
					onChange={(subCompanyId) =>
						onChangeAttribute({ subCompanyId: subCompanyId })
					}
				>
					{subCompanies.map((subCompany) => {
						const isMismatched =
							currentUser?.subCompany?.id === subCompany.id &&
							currentUser?.subCompany?.companyId !== currentUser.companyId;
						return (
							<Select.Option
								key={subCompany.id}
								value={subCompany.id}
								label={subCompany.name}
								style={isMismatched ? { color: 'red' } : {}}
							>
								{subCompany.name}
							</Select.Option>
						);
					})}
				</Select>
			</div>
			<div>
				<div className="elevated-permissions-label-container">
					<span className="elevated-permissions-label-name">User Group</span>
					<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
						<ThunderboltOutlined
							className="elevated-permissions-label-copy"
							onClick={() => toggleAutoAssign('userGroup')}
							style={{
								color: autoAssign.userGroup ? '#6cb761' : '#05031be6',
							}}
							title={`Auto-assign User Group (looks for "Default") - Currently: ${autoAssign.userGroup ? 'ON' : 'OFF'}`}
							onMouseEnter={(e) => (e.currentTarget.style.color = '#1e9e28e6')}
							onMouseLeave={(e) =>
								(e.currentTarget.style.color = autoAssign.userGroup
									? '#6cb761'
									: '#05031be6')
							}
						/>
						<CopyOutlined
							className="elevated-permissions-label-copy"
							onClick={(e) => handleCopyId(e, 'userGroupId')}
							title={`Copy User Group ID: ${currentUser?.userGroupId}`}
							onMouseEnter={(e) => (e.currentTarget.style.color = '#1e9e28e6')}
							onMouseLeave={(e) => (e.currentTarget.style.color = '#05031be6')}
						/>
					</div>
				</div>
				<Select
					showSearch
					style={{
						width: '100%',
						...(currentUser?.userGroup?.companyId &&
						currentUser?.userGroup?.companyId !== currentUser.companyId
							? { color: 'red' }
							: {}),
					}}
					placeholder="Select User Group"
					optionFilterProp="children"
					value={currentUser.userGroupId}
					suffixIcon={<DownOutlined />}
					getPopupContainer={() =>
						document.getElementById('elevated-permissions-container')
					}
					dropdownStyle={{ zIndex: 10001 }}
					onChange={(userGroupId) =>
						onChangeAttribute({ userGroupId: userGroupId })
					}
				>
					{userGroups.map((userGroup) => {
						const isMismatched =
							currentUser?.userGroup?.id === userGroup.id &&
							currentUser?.userGroup?.companyId !== currentUser.companyId;
						return (
							<Select.Option
								key={userGroup.id}
								value={userGroup.id}
								label={userGroup.name}
								style={isMismatched ? { color: 'red' } : {}}
							>
								{userGroup.name}
							</Select.Option>
						);
					})}
				</Select>
			</div>
		</div>
	);
};

export default ElevatedPermissionsControls;
